// import React from 'react';
import { useTheme } from "@emotion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetStationDetailById, GetFinalStationList } from "api/stations";
import useStationRefresh from "hooks/useStationRefresh";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { GetCustomersList } from "api/customers";
import { getAllCustomerGroups } from "api/customerGroup";
import {
  GetReadyToPickupPackages,
  GetSumReadyToPickupPackages,
  PatchPackageDelivery,
} from "api/packages";
import Swal from "sweetalert2";
import { grey } from "@mui/material/colors";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";

import { alpha } from "@mui/material/styles";
import Card from "@mui/material/Card";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import FlexBetween from "components/FlexBetween";
import { copyTextToClipboard, playCorrectSound } from "utils";
import { Edit } from "@mui/icons-material";
import { StoreTrackingLog } from "api/trackingLogs";
import { GetLatestRate } from "api/currencyExchangeRate";
import PackageEditDialog from "components/PackageEditDialog";
import ShippingPaymentDialog from "components/ShippingPaymentDialog";
import TrackingDetailDialog from "components/TrackingDetialDialog.";
import WalletDebtBadge from "components/WalletDebtBadge";
import WalletHistoryDialog from "components/WalletHistoryDialog";
import { hasPageAccess } from "utils/pagePermissions";

const MONO = "'Space Grotesk', ui-monospace, monospace";
// Neutral design tokens (not brand color — kept literal to match the design's exact grays/borders)
const TEXT_BODY = "#2c2937";
const TEXT_FAINT = "#a8a3b4";
const BORDER = "#ece9f1";
const GROUP_BG = "#faf9fc";
const GROUP_BORDER = "#f0eef5";
const CHEVRON = "#b9b4c4";

const initials = (name) => {
  const p = (name || "?").trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : (name || "?").slice(0, 2)).toUpperCase();
};

function SelfPickup() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [stationList, setStationList] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [nextStation, setNextStation] = useState("");
  const [rows, setRows] = useState(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  // Package data
  const [packageData, setPackageData] = useState(null);

  // Data for front-end
  const [customer, setCustomer] = useState({});
  const [searchCustomer, setSearchCustomer] = useState("");
  const [customerList, setCustomerList] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [selectedTrackingNumber, setSelectedTrackingNumber] = useState("");
  const [walletHistoryOpen, setWalletHistoryOpen] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalShippingFee, setTotalShippingFee] = useState(0);
  const [currencyExchangeRate, setCurrencyExchangeRate] = useState(null);
  const [feeUSD, setFeeUSD] = useState(0);
  const [feeLAK, setFeeLAK] = useState(0);

  const { user: _user, defaultStation: _defaultStation, ready } = useStationRefresh();
  const userStations = Array.isArray(_user?.stations) ? _user.stations : [];
  const isAgentUser = _user?.role === "agent";
  // Bypasses the mandatory delivery-proof-photo collect flow (ShippingPaymentDialog) —
  // restricted so it isn't a silent workaround for staff who should be going through
  // that flow. Hidden entirely for users without the permission (see shared/pageSlugs.json).
  const canMarkDelivered = hasPageAccess(_user, "whatsapp/self-pickup/mark-delivered");

  useEffect(() => {
    GetFinalStationList().then((res) => {
      if (res && res.success) {
        let arr = Array.isArray(res.data) ? res.data : [];
        let validStation = [];
        if (Array.isArray(arr)) {
          arr.forEach((finalStation) => {
            userStations.forEach((userStation) => {
              if (finalStation.id === userStation.id)
                validStation.push(finalStation);
            });
          });
        }
        const availableStations = validStation.length > 0 ? validStation : arr;
        setStationList(availableStations);

        const initialStationId = _defaultStation?.id || availableStations[0]?.id;
        if (initialStationId) {
          GetStationDetailById(initialStationId).then((res) => {
            if (res && res.success) {
              setSelectedStation(res.data);
            }
          });
        }
      }

      GetStationDetailById("END").then((res) => {
        if (res && res.success) {
          setNextStation(res.data);
        }
      });
    });
    GetLatestRate().then((res) => {
      if (res && res.status === 200) {
        setCurrencyExchangeRate(res.data);
      }
    });

    // Fetch customer groups
    getAllCustomerGroups().then((res) => {
      if (res && res.success) {
        setCustomerGroups(res.data);
      }
    });
    // Re-runs once the refreshed profile/default station lands (`ready`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    if (searchCustomer) {
      GetCustomersList(searchCustomer).then((res) => {
        if (res && res.success) {
          setCustomerList(res.data);
        } else {
          console.log(res.message);
        }
      });
    }
  }, [searchCustomer]);

  useEffect(() => {
    if (customer?._id && selectedStation?.id) {
      GetReadyToPickupPackages(customer._id, selectedStation.id).then((res) => {
        if (res && res.status === 200) {
          let packages = res.data.packages;
          //define obj.sendStatus
          packages.forEach((item, index) => {
            packages[index].sendStatus = true;
          });

          setRows(packages);
        } else {
          const errMsg = res?.message || "Failed to load packages";
          Swal.fire({
            title: "API call error",
            text: errMsg,
            icon: "error",
          });
        }
      });
    }
  }, [customer, selectedStation]);

  const handleSendStatus = (index) => {
    // Clone state => rows
    const newList = [...rows];
    newList.forEach((packageItem, i) => {
      // if packageItem index === index ? set sendStatus to opposite value else return current value
      return (newList[i].sendStatus =
        i === index ? !packageItem.sendStatus : packageItem.sendStatus);
    });
    setRows(newList);
  };

  const handleMarkAllReady = () => {
    setRows((prev) =>
      Array.isArray(prev) ? prev.map((p) => ({ ...p, sendStatus: true })) : prev
    );
  };

  // Helper function to get customer group information
  const getCustomerGroup = () => {
    if (customer.groupId && customerGroups.length > 0) {
      return customerGroups.find(group => group.id === customer.groupId) || null;
    }
    return null;
  };

  const handleCopyButtonClicked = () => {
    console.log("Copy button clicked!");
    let whatsappNumber = customer?.whatsapp;
    let sendItems = rows.filter((item) => item.sendStatus === true);
    let trackingNumbers = sendItems.map((item) => item.tracking);
    GetSumReadyToPickupPackages(
      customer._id,
      trackingNumbers,
      selectedStation.id,
      false
    ).then((res) => {
      // console.log(res);
      if (res && res.status === 200) {
        copyTextToClipboard(res.data.smsMessage);
        Swal.fire({
          text: "Copy message success",
          icon: "success",
          position: "top-right",
          timer: 1000,
          toast: true,
          showConfirmButton: false,
          didClose: () => {
            // Add didClose callback
            window.open(`https://wa.me/${whatsappNumber}`, "_blank"); // Open the link
          },
        });
      } else {
        Swal.fire({
          text: "Copy message failed",
          icon: "error",
          position: "top-right",
          timer: 5000,
          toast: true,
          showConfirmButton: true,
        });
      }
    });
  };

  const handleSuccessButtonClicked = (_id, tracking, remark) => {
    // console.log(_id,tracking,remark);
    Swal.fire({
      icon: "question",
      title: tracking,
      text: `Update the status of this package to  end?`,
      showConfirmButton: true,
      confirmButtonText: "Yes",
      showDenyButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        PatchPackageDelivery(
          _id,
          nextStation.id,
          nextStation.parcelStatus,
          true,
          true,
          "Self-pickup"
        ).then((res) => {
          let actionBy = _user?.name || "system";
          if (res && res.success) {
            StoreTrackingLog(
              _id,
              tracking,
              nextStation.name,
              nextStation.message,
              remark,
              actionBy
            ).then((res) => {
              if (res && res.status === 200) {
                // DELETE SELECTED ROW FROM ARRAY
                setRows(() => {
                  return rows.filter((row) => row._id !== _id);
                });
                playCorrectSound();
                Swal.fire({
                  icon: "success",
                  title: `Success!`,
                  text: `Tracking no. ${tracking} has been updated`,
                  timer: 1500,
                });
              }
            });
          }
        });
      }
    });
  };

  const buttonEditHandler = (rowValue) => {
    setPackageData(rowValue);
    setEditDialogOpen(true);
  };

  // After an edit saves, drop any package that no longer matches the current
  // list filter (same criteria the backend uses to build the list — see
  // getReadyToPickupPackagesByCustomerId). e.g. the customer was reassigned or
  // the station/whatsapp/payment status changed → it falls off this view.
  const reconcileRowsAfterEdit = () => {
    setRows((prev) =>
      Array.isArray(prev)
        ? prev.filter(
            (p) =>
              p.customer?._id === customer?._id &&
              String(p.station ?? "") === String(selectedStation?.id ?? "") &&
              p.paymentStatus === false &&
              p.whatsappStatus === true
          )
        : prev
    );
  };

  // Parcels switched ON and not already locked by a pending approval — the set the
  // collect flow settles. Fee due = shipping fee + COD `amount` (matches the FEE THB
  // KPI and what the backend now collects). Pickup/Delivery service fee is chosen
  // inside ShippingPaymentDialog now, so this is the package-only base fee.
  const collectablePackages = Array.isArray(rows)
    ? rows.filter((r) => r.sendStatus === true && !r.pendingShippingPaymentId)
    : [];
  const collectFeeTHB = collectablePackages.reduce(
    (sum, r) => sum + (parseInt(r.shippingFee ?? 0) || 0) + (parseInt(r.amount ?? 0) || 0),
    0
  );

  const handleCollectClick = () => {
    if (!customer?._id || !selectedStation?.id) {
      Swal.fire({ icon: "warning", text: t("pickup.payment.selectFirst"), timer: 1800 });
      return;
    }
    if (collectablePackages.length === 0) {
      Swal.fire({ icon: "info", text: t("pickup.payment.nothingToCollect"), timer: 1800 });
      return;
    }
    setPaymentDialogOpen(true);
  };

  const handleCollectSuccess = (status) => {
    setPaymentDialogOpen(false);
    const collectedIds = new Set(collectablePackages.map((p) => p._id));
    setRows((prev) =>
      Array.isArray(prev) ? prev.filter((r) => !collectedIds.has(r._id)) : prev
    );
    playCorrectSound();
    Swal.fire({
      icon: "success",
      text: status === "pending_approval"
        ? t("pickup.payment.submittedForApproval")
        : t("pickup.payment.collected"),
      toast: true,
      position: "top-right",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  useEffect(() => {
    // console.log("rows is updated", rows);
    if (rows) {
      let sendItems = rows.filter((item) => item.sendStatus === true);
      setTotalItems(sendItems.length);
      setTotalShippingFee(() => {
        return sendItems.reduce((accumulator, object) => {
          if (isAgentUser) {
            return (
              parseInt(accumulator) +
              parseInt(object.finalShippingFee ?? 0) +
              parseInt(object.amount ?? 0)
            );
          } else {
            return (
              parseInt(accumulator) +
              parseInt(object.shippingFee ?? 0) +
              parseInt(object.amount ?? 0)
            );
          }
        }, 0);
      });
    }
  }, [rows, isAgentUser]);

  useEffect(() => {
    if (totalShippingFee > 0 && currencyExchangeRate) {
      setFeeLAK(() => {
        let num =
          parseInt(totalShippingFee) * parseInt(currencyExchangeRate.LAK);
        let roundedLAK = Math.round(num / 1000) * 1000;
        return roundedLAK.toLocaleString();
      });

      setFeeUSD(() => {
        let num =
          parseInt(totalShippingFee) / parseInt(currencyExchangeRate.USD);
        return num.toFixed(2);
      });
    } else {
      // No payable items (cleared/zero THB) → keep LAK + USD in sync with THB.
      setFeeLAK(0);
      setFeeUSD(0);
    }
  }, [totalShippingFee, currencyExchangeRate]);

  // ---- redesign style tokens (brand purple comes from theme.palette.secondary,
  // which now mirrors the Claude Design hex exactly — see theme.js) ----
  const accent = theme.palette.secondary.main;
  const accentLight = theme.palette.secondary.light;
  const accentDark = theme.palette.secondary.dark;
  const tintBg = alpha(accent, 0.06);
  const PANEL_RADIUS = "20px";
  const KPI_RADIUS = "18px";
  const CARD_RADIUS = "20px";
  const softShadow =
    "0 1px 2px rgba(20,18,30,.05), 0 10px 28px -20px rgba(20,18,30,.22)";
  const cardShadow =
    "0 1px 2px rgba(20,18,30,.05), 0 12px 28px -18px rgba(20,18,30,.24)";
  const iconBtnSx = {
    width: 32,
    height: 32,
    border: `1px solid ${BORDER}`,
    borderRadius: "9px",
    color: accent,
    "&:hover": { background: tintBg, borderColor: alpha(accent, 0.45) },
  };
  const readyCount = Array.isArray(rows)
    ? rows.filter((r) => r.sendStatus === true).length
    : 0;
  const totalCount = Array.isArray(rows) ? rows.length : 0;
  const customerSelected = !!customer?._id;

  return (
    <Box>
      {/* filter panel */}
      <Paper
        elevation={0}
        sx={{
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: PANEL_RADIUS,
          p: { xs: 2, md: "20px 24px" },
          boxShadow: softShadow,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          {/* customer */}
          <Box sx={{ flex: 1, minWidth: { md: 260 } }}>
            <Autocomplete
              onInput={(e) => setSearchCustomer(e.target.value)}
              onChange={(e, value) => {
                if (value) {
                  setCustomer({
                    _id: value._id,
                    id: value.id,
                    name: value.name,
                    whatsapp: value.whatsapp,
                    groupId: value.groupId, // Added missing groupId field
                    pr_id: value.province?.pr_id,
                  });
                }
              }}
              disablePortal
              id="combo-box-customer"
              options={customerList}
              isOptionEqualToValue={(customerList, customer) =>
                customerList._id === customer._id
              }
              getOptionLabel={(option) =>
                option.name + " - " + option.whatsapp || ""
              }
              popupIcon={<ExpandMoreIcon sx={{ color: CHEVRON }} />}
              sx={{ "& .MuiAutocomplete-clearIndicator": { color: CHEVRON } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={t("pickup.selfPickup.customer")}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    sx: {
                      fontSize: "10.5px",
                      letterSpacing: ".06em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: TEXT_FAINT,
                      background: theme.palette.background.paper,
                      px: 0.5,
                      "&.Mui-focused": { color: TEXT_FAINT },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: customerSelected ? (
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "8px",
                          background: `linear-gradient(135deg, ${accentLight}, ${accentDark})`,
                          color: "#fff",
                          fontFamily: MONO,
                          fontWeight: 700,
                          fontSize: 11,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: "none",
                          mr: 1,
                        }}
                      >
                        {initials(customer.name)}
                      </Box>
                    ) : null,
                    sx: {
                      height: 46,
                      borderRadius: "12px",
                      background: customerSelected ? tintBg : theme.palette.background.paper,
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: TEXT_BODY,
                      "& fieldset": {
                        borderColor: customerSelected ? alpha(accent, 0.35) : BORDER,
                        borderWidth: "1.5px",
                      },
                      "&:hover fieldset": { borderColor: alpha(accent, 0.35) },
                      "&.Mui-focused fieldset": { borderColor: alpha(accent, 0.5) },
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* station */}
          <FormControl sx={{ minWidth: { md: 200 } }} fullWidth>
            <InputLabel
              sx={{
                fontSize: "10.5px",
                letterSpacing: ".06em",
                textTransform: "uppercase",
                fontWeight: 700,
                color: TEXT_FAINT,
                background: theme.palette.background.paper,
                px: 0.5,
                "&.Mui-focused": { color: TEXT_FAINT },
              }}
            >
              {t("pickup.selfPickup.selectStation")}
            </InputLabel>
            <Select
              label={t("pickup.selfPickup.selectStation")}
              value={selectedStation ? selectedStation.id : ""}
              onChange={(e) => {
                GetStationDetailById(e.target.value).then((res) => {
                  if (res.success) {
                    setSelectedStation(res.data);
                  }
                });
              }}
              IconComponent={(props) => <ExpandMoreIcon {...props} sx={{ color: CHEVRON }} />}
              required
              sx={{
                height: 46,
                borderRadius: "12px",
                fontSize: 13.5,
                fontWeight: 600,
                color: TEXT_BODY,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER, borderWidth: "1.5px" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha(accent, 0.35) },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: alpha(accent, 0.5) },
              }}
            >
              {stationList &&
                stationList.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {id} - {name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* copy + complete */}
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ContentCopyIcon sx={{ fontSize: 18 }} />}
              onClick={handleCopyButtonClicked}
              sx={{
                height: 46,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: 13,
                borderWidth: "1.5px",
                px: 2.5,
                flex: { xs: 1, md: "none" },
                "&:hover": { borderWidth: "1.5px" },
              }}
            >
              {t("pickup.selfPickup.copy")}
            </Button>
            {!isAgentUser && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<VerifiedOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={handleCollectClick}
                disabled={collectablePackages.length === 0}
                sx={{
                  height: 46,
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: 13,
                  px: 3,
                  flex: { xs: 1, md: "none" },
                  background: `linear-gradient(135deg, ${accentLight}, ${accentDark})`,
                  boxShadow: `0 4px 14px -4px ${alpha(accent, 0.6)}`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${accentLight}, ${accentDark})`,
                    opacity: 0.92,
                  },
                }}
              >
                {t("pickup.payment.completeCollect")}
              </Button>
            )}
          </Stack>
        </Stack>

        {/* customer group info */}
        {customer._id &&
          (() => {
            const customerGroup = getCustomerGroup();
            const status = customerGroup?.whatsappStatus?.status;
            return (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{
                  mt: 1.75,
                  px: 1.75,
                  py: 1.25,
                  background: GROUP_BG,
                  border: `1px solid ${GROUP_BORDER}`,
                  borderRadius: "11px",
                }}
              >
                <Typography sx={{ fontSize: 12.5, color: "#7c7787" }}>
                  {t("pickup.selfPickup.customerGroup")}:{" "}
                  <Box component="strong" sx={{ color: TEXT_BODY, fontWeight: 600 }}>
                    {customerGroups.length === 0
                      ? t("pickup.selfPickup.loadingGroups")
                      : customerGroup?.name ||
                        `${t("pickup.selfPickup.noGroup")} (ID: ${
                          customer.groupId || "none"
                        })`}
                  </Box>
                </Typography>
                {status && (
                  <Box
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: "#fff",
                      background:
                        status === "ready"
                          ? "linear-gradient(135deg,#16a34a,#15803d)"
                          : "linear-gradient(135deg,#9ca3af,#6b7280)",
                      px: 1.25,
                      py: 0.25,
                      borderRadius: "999px",
                    }}
                  >
                    {status}
                  </Box>
                )}
                <WalletDebtBadge customerId={customer._id} onClick={() => setWalletHistoryOpen(true)} />
              </Stack>
            );
          })()}
      </Paper>

      {/* summary KPI strip */}
      <Box
        sx={{
          mt: 2,
          display: "grid",
          gap: 1.75,
          gridTemplateColumns: {
            xs: "1fr 1fr",
            md: "repeat(4, 1fr)",
          },
        }}
      >
        {[
          {
            label: t("pickup.selfPickup.totalItems"),
            value: totalItems,
            hint: t("pickup.selfPickup.readyToCollect"),
            big: true,
          },
          {
            label: t("pickup.selfPickup.feeThb"),
            value: `฿${Number(totalShippingFee || 0).toLocaleString()}`,
            hint: t("pickup.selfPickup.totalPayable"),
            highlight: true,
            accentValue: true,
          },
          {
            label: t("pickup.selfPickup.feeLak"),
            value: `₭${feeLAK || 0}`,
            hint: t("pickup.selfPickup.totalPayable"),
          },
          {
            label: t("pickup.selfPickup.feeUsd"),
            value: `$${feeUSD || 0}`,
            hint: t("pickup.selfPickup.totalPayable"),
          },
        ].map((kpi, i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{
              borderRadius: KPI_RADIUS,
              p: "16px 20px",
              border: `1px solid ${
                kpi.highlight ? alpha(accent, 0.25) : theme.palette.divider
              }`,
              background: kpi.highlight
                ? `linear-gradient(155deg, ${theme.palette.background.paper} 0%, ${alpha(
                    accent,
                    0.09
                  )} 100%)`
                : theme.palette.background.paper,
              boxShadow: softShadow,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                letterSpacing: ".14em",
                fontWeight: 700,
                textTransform: "uppercase",
              }}
              color="text.secondary"
            >
              {kpi.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Space Grotesk', monospace",
                fontWeight: 700,
                fontSize: kpi.big ? 30 : 24,
                mt: 0.5,
                lineHeight: 1.1,
              }}
              color={kpi.accentValue ? "secondary.main" : "text.primary"}
            >
              {kpi.value}
            </Typography>
            <Typography sx={{ fontSize: 11.5, mt: 0.25 }} color="text.secondary">
              {kpi.hint}
            </Typography>
          </Paper>
        ))}
      </Box>
      {/* section header */}
      <FlexBetween sx={{ mt: 3.5, mb: 2 }}>
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: ".1em",
              textTransform: "uppercase",
            }}
            color="text.secondary"
          >
            {t("pickup.selfPickup.packages")}
          </Typography>
          <Typography sx={{ fontSize: 12, mt: 0.5 }} color="text.secondary">
            {t("pickup.selfPickup.markedReady", {
              ready: readyCount,
              total: totalCount,
            })}
          </Typography>
        </Box>
        {totalCount > 0 && (
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={handleMarkAllReady}
            sx={{ borderRadius: "9px", fontWeight: 700, textTransform: "none" }}
          >
            {t("pickup.selfPickup.markAllReady")}
          </Button>
        )}
      </FlexBetween>

      {/* package grid */}
      <Grid container spacing={2.25}>
        {rows &&
          rows.map((packageItem, index) => {
            var isSubCustomer = packageItem.isSubCustomer;
            var finalShippingFee = parseInt(packageItem.finalShippingFee ?? 0);
            var shippingFee = parseInt(packageItem.shippingFee ?? 0);
            var amount = parseInt(packageItem.amount ?? 0);
            var totalFee = isSubCustomer
              ? parseInt(amount) + parseInt(finalShippingFee)
              : parseInt(amount) + parseInt(shippingFee);
            const ready = packageItem.sendStatus;
            const badgeBg =
              totalFee > 100
                ? `linear-gradient(135deg, ${accent}, ${accentDark})`
                : totalFee > 0
                ? `linear-gradient(135deg, ${alpha(accent, 0.82)}, ${accent})`
                : grey[400];

            return (
              <Grid item key={index} xl={6} md={6} xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    background: theme.palette.background.paper,
                    border: `1px solid ${
                      ready ? alpha(accent, 0.35) : theme.palette.divider
                    }`,
                    borderRadius: CARD_RADIUS,
                    boxShadow: cardShadow,
                    transition: "border-color .2s, box-shadow .2s",
                    "&:hover": {
                      borderColor: alpha(accent, 0.45),
                      boxShadow: `0 1px 2px rgba(20,18,30,.05), 0 18px 36px -18px ${alpha(
                        accent,
                        0.28
                      )}`,
                    },
                  }}
                >
                  <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
                    {/* header row */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1.5}
                    >
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ minWidth: 0 }}
                      >
                        <Avatar
                          variant="rounded"
                          aria-label="shipping fee"
                          sx={{
                            width: 46,
                            height: 46,
                            borderRadius: "14px",
                            background: badgeBg,
                            color: "#fff",
                            fontFamily: "'Space Grotesk', monospace",
                            fontWeight: 700,
                            fontSize: String(totalFee).length > 3 ? 13 : 16,
                          }}
                        >
                          {totalFee}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Link
                            component="button"
                            type="button"
                            color="secondary"
                            underline="hover"
                            onClick={() => {
                              setSelectedTrackingNumber(packageItem.tracking);
                              setTrackingDialogOpen(true);
                            }}
                            sx={{
                              display: "block",
                              fontFamily: "'Space Grotesk', monospace",
                              fontWeight: 700,
                              fontSize: 14.5,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%",
                              textAlign: "left",
                            }}
                          >
                            {packageItem.tracking}
                          </Link>

                          <Typography
                            sx={{
                              fontSize: 11.5,
                              mt: 0.25,
                              fontFamily: "'Space Grotesk', monospace",
                            }}
                            color="text.secondary"
                          >
                            {moment(packageItem.updatedAt)
                              .tz("Asia/Vientiane")
                              .format("DD/MM/YYYY, hh:mm:ss")}
                          </Typography>
                        </Box>
                      </Stack>
                      {/* toggle */}
                      <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                        <Switch
                          color="secondary"
                          checked={ready}
                          onChange={() => handleSendStatus(index)}
                        />
                        <Typography
                          sx={{ fontSize: 10.5, fontWeight: 700 }}
                          color={ready ? "secondary.main" : "text.secondary"}
                        >
                          {ready
                            ? t("pickup.selfPickup.ready")
                            : t("pickup.selfPickup.notReady")}
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* order id */}
                    <Box
                      sx={{
                        mt: 1.75,
                        px: 1.5,
                        py: 1.25,
                        background: tintBg,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "11px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: ".06em",
                          textTransform: "uppercase",
                          mb: 0.5,
                        }}
                        color="text.secondary"
                      >
                        {t("pickup.selfPickup.orderId")}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Space Grotesk', monospace",
                          fontSize: 12.5,
                          fontWeight: 600,
                          wordBreak: "break-all",
                        }}
                        color="secondary.main"
                      >
                        {packageItem.orderId
                          ? packageItem.orderId
                          : t("pickup.selfPickup.noOrderId")}
                      </Typography>
                    </Box>

                    {/* description */}
                    {packageItem.description && (
                      <Typography
                        sx={{
                          mt: 1.25,
                          fontSize: 12.5,
                          lineHeight: 1.55,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        color="text.secondary"
                      >
                        {packageItem.description}
                      </Typography>
                    )}

                    {/* remark */}
                    {packageItem.remark && (
                      <Typography
                        sx={{ mt: 1, fontSize: 12.5 }}
                        color="secondary.main"
                      >
                        {t("pickup.selfPickup.remark")}: {packageItem.remark}
                      </Typography>
                    )}

                    {/* actions */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      sx={{
                        mt: "auto",
                        pt: 1.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {canMarkDelivered && (
                        <IconButton
                          size="small"
                          aria-label="update status to success/done"
                          onClick={() =>
                            handleSuccessButtonClicked(
                              packageItem._id,
                              packageItem.tracking,
                              packageItem.remark
                            )
                          }
                          sx={iconBtnSx}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        aria-label="Edit package"
                        onClick={() => buttonEditHandler(packageItem)}
                        sx={iconBtnSx}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <Typography
                        sx={{
                          ml: "auto",
                          fontSize: 11,
                          fontFamily: "'Space Grotesk', monospace",
                        }}
                        color="text.secondary"
                      >
                        {moment(packageItem.updatedAt)
                          .tz("Asia/Vientiane")
                          .format("DD/MM/YYYY")}
                      </Typography>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
      </Grid>

      {/* Edit Package Dialog */}
      <Box>
        <PackageEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          packageData={packageData}
          rows={rows}
          setRows={setRows}
          onSuccess={reconcileRowsAfterEdit}
          isAgentUser={isAgentUser}
        />
        <ShippingPaymentDialog
          open={paymentDialogOpen}
          onClose={() => setPaymentDialogOpen(false)}
          customerId={customer?._id}
          stationId={selectedStation?.id}
          packageIds={collectablePackages.map((p) => p._id)}
          baseFeeTHB={collectFeeTHB}
          currencyExchangeRate={currencyExchangeRate}
          onSuccess={handleCollectSuccess}
        />
        <TrackingDetailDialog
          open={trackingDialogOpen}
          onClose={() => setTrackingDialogOpen(false)}
          trackingNumber={selectedTrackingNumber}
        />
        <WalletHistoryDialog
          open={walletHistoryOpen}
          onClose={() => setWalletHistoryOpen(false)}
          customerId={customer?._id}
          customerName={customer?.name}
        />
        {/* <Dialog
          open={editDialogOpen}
          TransitionComponent={Transition}
          maxWidth="sm"
          keepMounted
          disableEnforceFocus
          onClose={() => {
            setEditDialogOpen(false);
          }}
          aria-describedby="alert-dialog-slide-description"
        >
          <form onSubmit={handleFormSubmit}>
            <DialogTitle>
              Edit Package
              <IconButton
                aria-label="close"
                onClick={() => setEditDialogOpen(false)}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                }}
              >
                <CloseOutlined />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} mt={2}>
                <Grid item md={6} xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Tracking No.</InputLabel>
                    <OutlinedInput
                      type="text"
                      label="Tracking No."
                      id="trackingNo"
                      required
                      value={tracking}
                      onChange={(e) => setTracking(e.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Order ID</InputLabel>
                    <OutlinedInput
                      type="text"
                      label="Order ID"
                      id="orderId"
                      value={orderId}
                      onChange={(e) => setOrderId(e.target.value)}
                    />
                    <input value={_id} readOnly id="_id" hidden />
                  </FormControl>
                </Grid>
                <Grid item md={12} xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      multiline
                      minRows={3}
                      maxRows={9}
                      label="Description"
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </FormControl>
                </Grid>

                <Grid item md={12} xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      multiline
                      maxRows={5}
                      label="Remark"
                      id="remark"
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid item md={4} xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Amount</InputLabel>
                    <OutlinedInput
                      type="number"
                      label="Amount"
                      id="amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid item md={4} xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Quantity</InputLabel>
                    <OutlinedInput
                      type="number"
                      label="Quantity"
                      id="quantity"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid item md={4} xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Shipping Fee</InputLabel>
                    <OutlinedInput
                      type="number"
                      label="Shipping Fee"
                      id="shippingFee"
                      value={shippingFee}
                      onChange={(e) => setShippingFee(e.target.value)}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                mb={2}
                mr={2}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Grid item xl={4} md={4} xs={6}>
                  <FormControl fullWidth>
                    <Button
                      variant="contained"
                      type="submit"
                      size="large"
                      color="secondary"
                    >
                      Save
                    </Button>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        </Dialog> */}
      </Box>
    </Box>
  );
}

export default SelfPickup;
